import {
  Component,
  Output,
  EventEmitter,
  Input,
  ViewChild,
  OnInit,
  OnDestroy,
  AfterViewInit,
} from "@angular/core";
import { EvaluationService } from "src/app/shared/services/evaluation.service";
import { MmpConfirmPopupComponent } from "../mmp-confirm-popup/mmp-confirm-popup.component";
import { MatDialog } from "@angular/material/dialog";
import { MESSAGES } from "src/app/shared/constants/messages.constant";
import { MatTabGroup, MatTabsModule } from "@angular/material/tabs";
import { Subject, takeUntil } from "rxjs";
import { MatSnackBar } from "@angular/material/snack-bar";
import { CommonModule } from "@angular/common";
import { MmpAiSummaryComponent } from "../mmp-ai-summary/mmp-ai-summary.component";
import { MmpFeedbackComponent } from "../mmp-feedback/mmp-feedback.component";
import { TitleCasePipe } from "src/app/pipes/title-case.pipe";
import { ComparisonNormalizer } from "src/app/shared/utils/comparison-normalizer.util";
import { AppConfigService } from "src/app/services/app-config.service";
import { MatTooltipModule } from "@angular/material/tooltip";

type CourseTabKey = "all" | "eligible" | "notEligible";

interface CourseTabOption {
  key: CourseTabKey;
  label: string;
}

@Component({
  selector: "app-mmp-evaluation",
  templateUrl: "./mmp-evaluation.component.html",
  styleUrls: ["./mmp-evaluation.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    MatTabsModule,
    MatTooltipModule,
    MmpAiSummaryComponent,
    MmpFeedbackComponent,
    TitleCasePipe,
  ],
})
export class MmpEvaluationComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  private readonly destroy$ = new Subject<void>();
  currentStage: string = "Evaluation";
  progressTagMsg: string = "In progress";
  aiSummaryCourseCount: number;
  transferForCredit: number;
  notTransferForCredit: number;
  aiSummaryConfidenceScore: number;
  evaluationResult: any;
  isTransferToggleChecked: boolean;
  showConsoleLogs: boolean = false;

  activeAccordion: number | null = null;
  currentCourseTab: CourseTabKey = "all";
  progressTimeText: string = "Evaluating...";
  progressTime!: string;
  disabledState: string = "none";
  ComparisonNormalizer = ComparisonNormalizer;
  readonly courseTabs: CourseTabOption[] = [
    { key: "all", label: MESSAGES.EVALUATION.ALL_COURSES_LABEL },
    {
      key: "eligible",
      label: MESSAGES.EVALUATION.ELIGIBLE_FOR_TRANSFER_LABEL,
    },
    {
      key: "notEligible",
      label: MESSAGES.EVALUATION.NOT_ELIGIBLE_FOR_TRANSFER_LABEL,
    },
  ];

  @Output() evaluationComplete = new EventEmitter<void>();
  @Input() selectedTranscriptIndex!: number;
  @Output() selectedTranscriptIndexChange = new EventEmitter<number>();
  @Output() backClick = new EventEmitter<string>();
  @Input() resizeComplete!: EventEmitter<void>;
  @ViewChild(MatTabGroup) tabGroup!: MatTabGroup;
  @Output() disableTabs = new EventEmitter<string>();
  @Output() inactiveTranscriptsIndexesChange = new EventEmitter<number[]>();

  statusShiftBannerVisible = false;
  statusShiftBannerText = "";
  statusShiftBannerTranscriptIndex: number | null = null;

  private statusShiftBannerTimeoutId: ReturnType<typeof setTimeout> | null =
    null;

  private lastStatusShift: {
    transcriptIndex: number;
    courseOrigins: number[];
    previousState: boolean;
    nextState: boolean;
  } | null = null;

  constructor(
    private readonly evaluationService: EvaluationService,
    private readonly confirmPopup: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly envService: AppConfigService,
  ) {
    this.showConsoleLogs = this.envService.showConsoleLogs;
  }

  ngOnInit(): void {
    const savedData = this.evaluationService.getEvaluationData();
    this.evaluationResult = this.normalizeEvaluationData(savedData);

    this.evaluationService.evaluationResult$
      .pipe(takeUntil(this.destroy$))
      .subscribe((transcripts) => {
        const parsedTranscripts = this.parseCompletedTranscripts(transcripts);
        if (parsedTranscripts.length === 0) {
          return;
        }

        const mergedTranscripts = this.mergeTranscriptsBySessionKey(
          this.evaluationResult?.transcripts || [],
          parsedTranscripts,
        );

        this.evaluationResult = {
          transcripts: this.decorateTranscriptsForView(mergedTranscripts),
        };
        this.evaluationService.setEvaluationData(this.evaluationResult);
      });

    this.evaluationService.evaluationSummary$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data) {
          this.aiSummaryCourseCount = data.ai_summary.total_courses;
          this.transferForCredit = data.ai_summary.transfer_for_credit;
          this.notTransferForCredit = data.ai_summary.not_transfer_for_credit;
          this.aiSummaryConfidenceScore = data.ai_summary.confidence_score;
          this.progressTime = data.ai_summary.total_processing_time;
          this.progressTimeText = "Processing Time:";
          this.progressTagMsg = "Completed";
        }
      });

    this.evaluationResult = {
      transcripts: this.decorateTranscriptsForView(
        this.evaluationResult?.transcripts || [],
      ),
    };

    if (this.showConsoleLogs) {
      console.log("evaluation result new", this.evaluationResult);
    }
  }

  private normalizeEvaluationData(data: any): any {
    return {
      transcripts: this.decorateTranscriptsForView(data?.transcripts || []),
    };
  }

  private parseCompletedTranscripts(transcripts: any[]): any[] {
    const completed = (transcripts || []).filter(
      (t) => ComparisonNormalizer.string(t.status) === "completed",
    );

    return completed
      .map((t) => {
        let parsed: any = {};
        try {
          parsed = JSON.parse(t.output);
        } catch (e) {
          if (this.showConsoleLogs) {
            console.log("Error parsing transcript output", e);
          }
          parsed = t.output;
        }

        if (!parsed?.result) {
          return null;
        }

        if (
          !parsed.result.pre_evaluation_passed &&
          parsed.result.pre_evaluation_passed !== "false"
        ) {
          return {
            name: parsed.name,
            university: parsed.university,
            transcript_rule_evaluation:
              parsed.result.transcript_rule_evaluation || [],
            session_key: parsed.session_key,
          };
        }
        return null;
      })
      .filter((t) => t !== null);
  }

  private mergeTranscriptsBySessionKey(
    existingTranscripts: any[],
    incomingTranscripts: any[],
  ): any[] {
    const normalizeSessionKey = (sessionKey: unknown): string =>
      ComparisonNormalizer.string(sessionKey).trim();

    const existingMap = new Map<string, any>();
    (existingTranscripts || []).forEach((transcript: any) => {
      const normalizedKey = normalizeSessionKey(transcript?.session_key);
      if (normalizedKey !== "") {
        existingMap.set(normalizedKey, transcript);
      }
    });

    const mergedInIncomingOrder = incomingTranscripts.map((incoming: any) => {
      const normalizedIncomingKey = normalizeSessionKey(incoming?.session_key);
      let existing;

      if (normalizedIncomingKey === "") {
        existing = undefined;
      } else {
        existing = existingMap.get(normalizedIncomingKey);
      }
      if (!existing) {
        return incoming;
      }

      return {
        ...incoming,
        transcript_rule_evaluation: this.mergeCourseSelections(
          existing.transcript_rule_evaluation || [],
          incoming.transcript_rule_evaluation || [],
        ),
      };
    });

    const incomingKeys = new Set(
      incomingTranscripts
        .map((transcript: any) => normalizeSessionKey(transcript?.session_key))
        .filter((sessionKey: string) => sessionKey !== ""),
    );
    const existingOnly = (existingTranscripts || []).filter(
      (transcript: any) =>
        !incomingKeys.has(normalizeSessionKey(transcript?.session_key)),
    );

    return [...mergedInIncomingOrder, ...existingOnly];
  }

  private mergeCourseSelections(
    existingCourses: any[],
    incomingCourses: any[],
  ): any[] {
    return incomingCourses.map((course: any, index: number) => {
      const existing = existingCourses[index] || {};
      return {
        ...course,
        selected: existing.selected ?? course.selected,
        userSelectedByAction: existing.userSelectedByAction ?? false,
        origin: existing.origin ?? course.origin ?? index,
        showRules: existing.showRules ?? false,
      };
    });
  }

  private decorateTranscriptsForView(transcripts: any[]): any[] {
    return (transcripts || []).map((transcript: any) => ({
      ...transcript,
      transcript_rule_evaluation: (
        transcript.transcript_rule_evaluation || []
      ).map((course: any, index: number) => ({
        ...course,
        userSelectedByAction: course.userSelectedByAction ?? false,
        origin: course.origin ?? index,
        showRules: course.showRules ?? false,
      })),
    }));
  }

  ngAfterViewInit(): void {
    // Listen for the resizeComplete event
    if (this.resizeComplete) {
      this.resizeComplete.pipe(takeUntil(this.destroy$)).subscribe(() => {
        if (this.tabGroup?._tabHeader) {
          this.tabGroup._tabHeader.updatePagination();
        }
      });
    }
  }

  toggleAccordion(index: number): void {
    this.activeAccordion = this.activeAccordion === index ? null : index;
  }

  onCourseCheckboxChange(
    transcriptIndex: number,
    courseIndex: number,
    event: Event,
  ): void {
    this.statusShiftBannerVisible = false;
    const checkbox = event.target as HTMLInputElement | null;
    const transcript = this.evaluationResult?.transcripts?.[transcriptIndex];
    const course = transcript?.transcript_rule_evaluation?.[courseIndex];

    if (!checkbox || !transcript || !course) {
      return;
    }
    course.userSelectedByAction = checkbox.checked;
    if (this.getUserSelectedCoursesCount(transcript) === 0) {
      this.disabledState = "none";
      return;
    }

    if (course.selected) {
      this.disabledState = "notSelected";
    } else if (!course.selected) {
      this.disabledState = "selected";
    }
  }

  private getBannerActionCourses(transcript: any): any[] {
    const courses = transcript?.transcript_rule_evaluation || [];

    if (this.disabledState === "selected") {
      return courses.filter((course: any) => !course.selected);
    }

    if (this.disabledState === "notSelected") {
      return courses.filter((course: any) => course.selected);
    }

    return [];
  }

  getSelectAllActionLabel(transcript: any): string {
    return "Select All";
  }

  selectAllSpecificCourses(transcriptIndex: number): void {
    const transcript = this.evaluationResult?.transcripts?.[transcriptIndex];
    const courses = this.getBannerActionCourses(transcript);

    if (!transcript || courses.length === 0) {
      return;
    }

    courses.forEach((course: any) => {
      course.userSelectedByAction = true;
    });
  }

  isCourseCheckboxDisabled(
    transcriptIndex: number,
    courseIndex: number,
  ): boolean {
    const transcript = this.evaluationResult?.transcripts?.[transcriptIndex];
    const course = transcript?.transcript_rule_evaluation?.[courseIndex];

    if (!course) {
      return false;
    }

    if (this.disabledState === "selected") {
      return !course.selected;
    }

    if (this.disabledState === "notSelected") {
      return course.selected;
    }

    return false;
  }

  getFilteredCourses(transcript: any) {
    const courses = transcript?.transcript_rule_evaluation || [];

    if (this.currentCourseTab === "eligible") {
      return courses.filter((course: any) => course.selected);
    }

    if (this.currentCourseTab === "notEligible") {
      return courses.filter((course: any) => !course.selected);
    }

    // Preserve the legacy toggle behavior if any existing flows still set it.
    if (this.isTransferToggleChecked) {
      return courses.filter((course: any) => course.selected);
    }

    return courses;
  }

  getCourseTabCount(transcript: any, tabKey: CourseTabKey): number {
    const courses = transcript?.transcript_rule_evaluation || [];
    const selectedCount = courses.filter(
      (course: any) => course.selected,
    ).length;

    if (tabKey === "all") {
      return courses.length;
    }

    if (tabKey === "eligible") {
      return selectedCount;
    }

    return Math.max(courses.length - selectedCount, 0);
  }

  isCourseTabActive(tabKey: CourseTabKey): boolean {
    return this.currentCourseTab === tabKey;
  }

  isTranscriptTabDisabled(transcriptIndex: number): boolean {
    if (!this.evaluationResult?.transcripts) {
      return false;
    }

    return this.evaluationResult.transcripts.some(
      (transcript: any) =>
        (transcript?.transcript_rule_evaluation || []).some(
          (course: any) => course.userSelectedByAction,
        ),
    );
  }

  getTotalCoursesCount(transcript: any): number {
    return (transcript?.transcript_rule_evaluation || []).length;
  }

  toggleRulesVisibility(course: any): void {
    course.showRules = !course.showRules;
  }

  onCourseTabChange(tabKey: CourseTabKey): void {
    this.currentCourseTab = tabKey;
    this.activeAccordion = null;
  }

  preventAccordionToggle(event: Event): void {
    event.stopPropagation();
  }

  // Calculate selected courses for a transcript, or across all transcripts when omitted
  getSelectedCoursesCount(transcript?: any): number {
    const getSelectedCount = (currentTranscript: any): number =>
      (currentTranscript?.transcript_rule_evaluation || []).filter(
        (course: any) => course.selected,
      ).length;

    if (transcript) {
      return getSelectedCount(transcript);
    }

    return (this.evaluationResult?.transcripts || []).reduce(
      (total: number, currentTranscript: any) =>
        total + getSelectedCount(currentTranscript),
      0,
    );
  }

  getUserSelectedCoursesCount(transcript?: any): number {
    return (transcript?.transcript_rule_evaluation || []).filter(
      (course: any) => course.userSelectedByAction,
    ).length;
  }

  getActiveTranscriptSelectionCount(): number {
    const transcript =
      this.evaluationResult?.transcripts?.[this.selectedTranscriptIndex];
    return this.getUserSelectedCoursesCount(transcript);
  }

  hasUserSelectedCourses(): boolean {
    return this.getActiveTranscriptSelectionCount() > 0;
  }

  clearSelectedCourses(): void {
    const transcript =
      this.evaluationResult?.transcripts?.[this.selectedTranscriptIndex];
    const courses = transcript?.transcript_rule_evaluation || [];

    if (!transcript || courses.length === 0) {
      return;
    }

    courses.forEach((course: any) => {
      course.userSelectedByAction = false;
    });
    this.disabledState = "none";
  }

  getCurrentTranscriptSelectedCourses(): any[] {
    const transcript =
      this.evaluationResult?.transcripts?.[this.selectedTranscriptIndex];
    return (transcript?.transcript_rule_evaluation || []).filter(
      (course: any) => course.userSelectedByAction,
    );
  }

  showStatusShiftConfirmation(): void {
    const selectedCourses = this.getCurrentTranscriptSelectedCourses();
    const isTransferAction = this.disabledState === "selected";

    if (selectedCourses.length === 0) {
      return;
    }

    const actionLabel = isTransferAction
      ? "Transfer for Credit"
      : "Non Transfer for Credit";
    const title = isTransferAction
      ? "Move to Transfer for Credit?"
      : "Move to Non Transfer for Credit?";
    const courseLabel =
      selectedCourses.length === 1 ? "Course" : "Courses";
    const courseDescriptor =
      selectedCourses.length === 1 ? "course" : "courses";
    const confirmText = isTransferAction
      ? `Move to Transfer for Credit (${selectedCourses.length} ${courseLabel})`
      : `Move to Non Transfer for Credit (${selectedCourses.length} ${courseLabel})`;

    const dialogRef = this.confirmPopup.open(MmpConfirmPopupComponent, {
      width: "auto",
      data: {
        title,
        message: `The following ${selectedCourses.length} ${courseDescriptor} will be changed to ${actionLabel}:`,
        issues: selectedCourses.map((course: any) => ({
          title: course.course_name || "Course",
          description: "",
        })),
        cancelText: "Cancel",
        confirmText,
        showWarning: true,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (ComparisonNormalizer.string(result) === "yes") {
          this.disableTabs.emit(this.currentStage.toLowerCase());
          selectedCourses.forEach((course: any) => {
            course.selected = isTransferAction;
            course.userSelectedByAction = false;
          });
          this.disabledState = "none";
          this.lastStatusShift = {
            transcriptIndex: this.selectedTranscriptIndex,
            courseOrigins: selectedCourses.map((course: any) => course.origin),
            previousState: !isTransferAction,
            nextState: isTransferAction,
          };
          this.showStatusShiftBanner(
            `${selectedCourses.length} ${selectedCourses.length === 1 ? "Course" : "Courses"} moved to ${actionLabel.toLowerCase()} successfully`,
            this.selectedTranscriptIndex,
          );
        }
      });
  }

  undoStatusShift(): void {
    if (!this.lastStatusShift) {
      this.clearStatusShiftBannerTimer();
      this.statusShiftBannerVisible = false;
      this.statusShiftBannerText = "";
      this.statusShiftBannerTranscriptIndex = null;
      return;
    }

    const { transcriptIndex, courseOrigins, previousState } =
      this.lastStatusShift;
    const transcriptCourses: any[] =
      this.evaluationResult?.transcripts?.[transcriptIndex]
        ?.transcript_rule_evaluation || [];

    transcriptCourses
      .filter((course: any) => courseOrigins.includes(course.origin))
      .forEach((course: any) => {
        course.selected = previousState;
        course.userSelectedByAction = false;
      });

    this.disabledState = "none";
    this.clearStatusShiftBannerTimer();
    this.statusShiftBannerVisible = false;
    this.statusShiftBannerText = "";
    this.statusShiftBannerTranscriptIndex = null;
    this.lastStatusShift = null;
  }

  handleSaveAndProceedClick(event: Event): void {
    if (this.getSelectedCoursesCount() === 0) {
      event.preventDefault();
      this.showToast(
        "toast-error",
        "No courses selected. Please select at least one course to continue",
      );
      return;
    }

    if (this.progressTagMsg === "Completed") {
      this.onSubmitEvaluation();
      return;
    }

    event.preventDefault();
    this.showToast(
      "toast-error",
      "Evaluation is still in progress. Please wait until it is completed to continue",
    );
  }

  onSubmitEvaluation() {
    const transcriptsWithoutSelections =
      this.evaluationResult.transcripts.filter(
        (transcript: any) => this.getSelectedCoursesCount(transcript) === 0,
      );
    const hasFalse = transcriptsWithoutSelections.length > 0;

    this.evaluationResult.transcripts.forEach((transcript: any) => {
      transcript.eligibleForProceeding =
        this.getSelectedCoursesCount(transcript) > 0;
    });

    const transcriptsWithoutSelectionsSet = new Set(
      transcriptsWithoutSelections,
    );
    const inactiveIndexes = this.evaluationResult.transcripts
      .map((transcript: any, index: number) =>
        transcriptsWithoutSelectionsSet.has(transcript) ? index : -1,
      )
      .filter((index: number) => index !== -1);
    this.inactiveTranscriptsIndexesChange.emit(inactiveIndexes);

    this.evaluationService.setEvaluationData(this.evaluationResult);

    if (hasFalse) {
      const transcriptNames = transcriptsWithoutSelections
        .map(
          (transcript) => transcript.name || transcript.university || "Unknown",
        )
        .join(", ");

      const dialogRef = this.confirmPopup.open(MmpConfirmPopupComponent, {
        width: "608px",
        data: {
          title: "Confirm To Proceed",
          message: MESSAGES.EVALUATION.CONTINUE_WARNING,
          issues: [
            {
              title: "Transcripts without selected courses",
              description: `Transcripts: ${transcriptNames}`,
            },
          ],
          cancelText: "Review Transcripts",
          confirmText: "Continue to Equivalency",
        },
      });

      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe((result) => {
          if (ComparisonNormalizer.string(result) === "yes") {
            this.evaluationCompleted();
          }
        });
    } else {
      this.evaluationCompleted();
    }
  }

  evaluationCompleted() {
    this.evaluationResult.transcripts.forEach((transcript) => {
      this.evaluationService.updateEvaluationOutput(
        transcript.session_key,
        transcript.transcript_rule_evaluation,
        transcript.eligibleForProceeding,
      );
    });
    this.evaluationComplete.emit();
  }

  goToExtractionTab() {
    this.backClick.emit("extraction");
  }

  showToast(type: string, message: string) {
    this.snackBar.open(message, "x", {
      duration: 3000,
      horizontalPosition: "right",
      verticalPosition: "top",
      panelClass: [type],
    });
  }

  ngOnDestroy(): void {
    this.clearStatusShiftBannerTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }

  getSelectedCoursesBannerText(transcript: any): string {
    const { selectedCount, courseLabel, currentState, actionState } =
      this.getSelectedCoursesBannerParts(transcript);

    return `Select all ${selectedCount} ${courseLabel} marked as ${currentState} to mark as ${actionState}`;
  }

  getSelectedCoursesBannerParts(transcript: any): {
    selectedCount: number;
    courseLabel: string;
    currentState: string;
    actionState: string;
  } {
    const selectedCount = this.getBannerActionCourses(transcript).length;

    return {
      selectedCount,
      courseLabel: selectedCount === 1 ? "course" : "courses",
      currentState:
        this.disabledState === "selected"
          ? "Not Eligible for Transfer"
          : "Eligible for Transfer",
      actionState:
        this.disabledState === "selected"
          ? "Eligible for Transfer"
          : "Not Eligible for Transfer",
    };
  }

  getSelectedCoursesBannerBody(
    transcript: any,
  ): { text: string; bold: boolean }[] {
    const { selectedCount, courseLabel, currentState, actionState } =
      this.getSelectedCoursesBannerParts(transcript);

    return [
      {
        text: `Select all ${selectedCount} ${courseLabel} marked as `,
        bold: false,
      },
      { text: currentState, bold: true },
      { text: " to mark as ", bold: false },
      { text: actionState, bold: true },
    ];
  }

  onTranscriptTabChange(event: number): void {
    this.activeAccordion = null;
    this.currentCourseTab = "all";
  }

  onTranscriptTabClick(event: Event): void {
    setTimeout(() => {
      this.selectedTranscriptIndexChange.emit(this.selectedTranscriptIndex);
    }, 10);
  }

  private clearStatusShiftBannerTimer(): void {
    if (this.statusShiftBannerTimeoutId !== null) {
      clearTimeout(this.statusShiftBannerTimeoutId);
      this.statusShiftBannerTimeoutId = null;
    }
  }

  private showStatusShiftBanner(
    message: string,
    transcriptIndex: number,
  ): void {
    this.clearStatusShiftBannerTimer();
    this.statusShiftBannerText = message;
    this.statusShiftBannerVisible = true;
    this.statusShiftBannerTranscriptIndex = transcriptIndex;
    this.statusShiftBannerTimeoutId = setTimeout(() => {
      this.statusShiftBannerVisible = false;
      this.statusShiftBannerText = "";
      this.statusShiftBannerTranscriptIndex = null;
      this.statusShiftBannerTimeoutId = null;
    }, 30000);
  }
}
